// @ts-check
// Chamadas da paridade de uso diário (docs/arquitetura-appcrm/08-contrato-paridade-uso-diario.md): duplicatas e fusão,
// lote da seleção, favoritos, views (criar e apagar) e arquivos de documentos, com o estado local do bootstrap.
import { api } from './api-client.js';
import { bootstrapState, normalizeView } from './metadata-store.js';
import { mutationCounterState } from './record-store.js';

/**
 * @typedef {import('../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('./metadata-store.js').View} View
 * @typedef {{ records: any[], errors: Array<{ id: string, code: string, message: string }> }} BatchResult
 */

// mesmo limite do motor por chamada (MAXIMUM_BATCH_RECORDS)
export const BATCH_SIZE = 200;
export const MAXIMUM_MERGE_RECORDS = 9;

/** @param {any} body @param {string} key */
const readData = (body, key) => body?.data?.[key] ?? body?.data ?? body;

/**
 * @param {ObjectMetadata} objectMetadata @param {string} recordId
 * @returns {Promise<any[]>}
 */
export async function findDuplicateRecords(objectMetadata, recordId) {
  const body = await api.get(`/rest/${objectMetadata.namePlural}/${recordId}/duplicates`);
  const records = readData(body, objectMetadata.namePlural);
  return Array.isArray(records) ? records : [];
}

/**
 * @param {ObjectMetadata} objectMetadata @param {string[]} ids @param {{ conflictPriorityIndex?: number, dryRun?: boolean }} [options]
 * @returns {Promise<{ record: any, mergedRecordIds: string[], dryRun: boolean }>}
 */
export async function mergeRecords(objectMetadata, ids, { conflictPriorityIndex = 0, dryRun = false } = {}) {
  const body = await api.post(`/rest/${objectMetadata.namePlural}/merge`, { ids, conflictPriorityIndex, dryRun });
  if (!dryRun) mutationCounterState.value += 1;
  return readData(body, 'mergeRecords');
}

/**
 * Divide a seleção em lotes do tamanho aceito pela API e junta os resultados.
 * @param {string[]} ids @param {(chunk: string[]) => Promise<BatchResult>} request @param {(done: number) => void} [onProgress]
 * @returns {Promise<BatchResult>}
 */
async function runInBatches(ids, request, onProgress) {
  /** @type {BatchResult} */
  const result = { records: [], errors: [] };
  for (let start = 0; start < ids.length; start += BATCH_SIZE) {
    const chunk = ids.slice(start, start + BATCH_SIZE);
    const partial = await request(chunk);
    result.records.push(...(partial?.records ?? []));
    result.errors.push(...(partial?.errors ?? []));
    onProgress?.(Math.min(ids.length, start + chunk.length));
  }
  mutationCounterState.value += 1;
  return result;
}

/**
 * @param {ObjectMetadata} objectMetadata @param {string[]} ids @param {Record<string, unknown>} data @param {(done: number) => void} [onProgress]
 */
export function updateManyRecords(objectMetadata, ids, data, onProgress) {
  return runInBatches(ids, async (chunk) => readData(await api.post(`/rest/${objectMetadata.namePlural}/batch-update`, { ids: chunk, data }), 'updateRecords'), onProgress);
}

/** @param {ObjectMetadata} objectMetadata @param {string[]} ids @param {(done: number) => void} [onProgress] */
export function deleteManyRecords(objectMetadata, ids, onProgress) {
  return runInBatches(ids, async (chunk) => readData(await api.post(`/rest/${objectMetadata.namePlural}/batch-delete`, { ids: chunk }), 'deleteRecords'), onProgress);
}

/** @param {ObjectMetadata} objectMetadata @param {string[]} ids */
export function restoreManyRecords(objectMetadata, ids) {
  return runInBatches(ids, async (chunk) => readData(await api.post(`/rest/${objectMetadata.namePlural}/batch-restore`, { ids: chunk }), 'restoreRecords'));
}

// Views

/** @param {any} view */
function addViewLocally(view) {
  const bootstrap = bootstrapState.value;
  if (!bootstrap) return;
  bootstrapState.value = { ...bootstrap, views: [...bootstrap.views.filter((candidate) => candidate.id !== view.id), normalizeView(view)] };
}

/**
 * @param {Record<string, unknown>} input
 * @returns {Promise<View>}
 */
export async function createView(input) {
  const body = await api.post('/rest/metadata/views', input);
  const view = normalizeView(readData(body, 'createView'));
  addViewLocally(view);
  return view;
}

/** @param {string} viewId */
export async function deleteView(viewId) {
  await api.delete(`/rest/metadata/views/${viewId}`);
  const bootstrap = bootstrapState.value;
  if (!bootstrap) return;
  bootstrapState.value = {
    ...bootstrap,
    views: bootstrap.views.filter((view) => view.id !== viewId),
    navigationMenuItems: bootstrap.navigationMenuItems.filter((item) => !(item.type === 'VIEW' && item.viewId === viewId && item.section === 'FAVORITES')),
  };
}

/**
 * Cópia da view (colunas, filtros, grupos de filtro, ordenações e agrupamento) com outro nome e visibilidade.
 * @param {View} view @param {{ name: string, visibility?: string, type?: string, icon?: string, calendarFieldMetadataId?: string | null,
 *   mainGroupByFieldMetadataId?: string | null, viewFilters?: any[], viewFilterGroups?: any[], viewSorts?: any[], anyFieldFilterValue?: string | null }} overrides
 */
export function buildViewCopy(view, overrides) {
  return {
    objectMetadataId: view.objectMetadataId,
    name: overrides.name,
    icon: overrides.icon ?? view.icon,
    type: overrides.type ?? view.type,
    visibility: overrides.visibility ?? 'WORKSPACE',
    mainGroupByFieldMetadataId: overrides.mainGroupByFieldMetadataId !== undefined ? overrides.mainGroupByFieldMetadataId : view.mainGroupByFieldMetadataId ?? null,
    calendarFieldMetadataId: overrides.calendarFieldMetadataId !== undefined ? overrides.calendarFieldMetadataId : view.calendarFieldMetadataId ?? null,
    kanbanAggregates: view.kanbanAggregates ?? undefined,
    pinnedCommandKeys: view.pinnedCommandKeys ?? [],
    openRecordIn: view.openRecordIn,
    // subFieldColumns: endereço em partes (CEP, Bairro, Cidade) vai junto na cópia
    viewFields: view.viewFields.map(({ fieldMetadataId, position, isVisible, size, aggregateOperation, subFieldColumns }) => ({
      fieldMetadataId, position, isVisible, size, aggregateOperation, ...(subFieldColumns ? { subFieldColumns } : {}),
    })),
    viewFilters: (overrides.viewFilters ?? view.viewFilters).map(({ fieldMetadataId, operand, value, subFieldName, viewFilterGroupId, positionInViewFilterGroup }) => ({
      fieldMetadataId, operand, value, subFieldName: subFieldName ?? null, viewFilterGroupId: viewFilterGroupId ?? null, positionInViewFilterGroup: positionInViewFilterGroup ?? null,
    })),
    viewFilterGroups: overrides.viewFilterGroups ?? view.viewFilterGroups ?? [],
    viewSorts: (overrides.viewSorts ?? view.viewSorts).map(({ fieldMetadataId, direction, subFieldName }) => ({
      fieldMetadataId, direction: String(direction).toLowerCase(), subFieldName: subFieldName ?? null,
    })),
    anyFieldFilterValue: overrides.anyFieldFilterValue !== undefined ? overrides.anyFieldFilterValue : view.anyFieldFilterValue ?? null,
    ...(view.type === (overrides.type ?? view.type) && view.viewGroups.length > 0
      ? { viewGroups: view.viewGroups.map(({ fieldValue, isVisible, position }) => ({ fieldValue, isVisible, position })) } : {}),
  };
}

// Favoritos

/** @param {{ type: 'VIEW', viewId: string } | { type: 'RECORD', objectNameSingular: string, recordId: string }} input */
export async function addFavorite(input) {
  const body = await api.post('/rest/metadata/favorites', input);
  const item = readData(body, 'createFavorite');
  const bootstrap = bootstrapState.value;
  if (bootstrap && item?.id) {
    bootstrapState.value = { ...bootstrap, navigationMenuItems: [...bootstrap.navigationMenuItems.filter((candidate) => candidate.id !== item.id), item] };
  }
  return item;
}

/** @param {string} itemId */
export async function removeFavorite(itemId) {
  await api.delete(`/rest/metadata/favorites/${itemId}`);
  const bootstrap = bootstrapState.value;
  if (bootstrap) bootstrapState.value = { ...bootstrap, navigationMenuItems: bootstrap.navigationMenuItems.filter((item) => item.id !== itemId) };
}

/** @param {string[]} orderedIds */
export async function reorderFavorites(orderedIds) {
  const bootstrap = bootstrapState.value;
  if (bootstrap) {
    const positions = new Map(orderedIds.map((id, index) => [id, index]));
    bootstrapState.value = {
      ...bootstrap,
      navigationMenuItems: bootstrap.navigationMenuItems.map((item) => (positions.has(item.id) ? { ...item, position: positions.get(item.id) } : item)),
    };
  }
  await api.put('/rest/metadata/favorites/order', { ids: orderedIds });
}

// Arquivos de documentos

/** @param {string} fileId */
export async function deleteUploadedFile(fileId) {
  return api.delete(`/rest/files/${fileId}`);
}
